import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css"; 
import "slick-carousel/slick/slick-theme.css";
import { API_BASE, getImageUrl } from '../utils/api';

interface Reel {
    _id: string;
    title: string;
    videoUrl: string;
    thumbnail: string;
    location: string;
}

const NextArrow = (props: any) => {
    const { onClick } = props;
    return (
        <button
            className="absolute top-[-45px] right-0 w-11 h-11 bg-[#05144b] text-white hover:bg-blue-700 rounded-2xl flex items-center justify-center transition-all duration-300 shadow-sm z-10"
            onClick={onClick}
        >
            <i className="fa-solid fa-chevron-right text-lg"></i>
        </button>
    );
}

const PrevArrow = (props: any) => {
    const { onClick } = props;
    return (
        <button
            className="absolute top-[-45px] right-14 w-11 h-11 bg-[#05144b] text-white hover:bg-blue-700 rounded-2xl flex items-center justify-center transition-all duration-300 shadow-sm z-10"
            onClick={onClick}
        >
            <i className="fa-solid fa-chevron-left text-lg"></i>
        </button>
    );
}

export default function ReelsSection() {
    const [reels, setReels] = useState<Reel[]>([])
    const [loading, setLoading] = useState(true)
    const [selectedReel, setSelectedReel] = useState<Reel | null>(null)

    useEffect(() => {
        fetch(`${API_BASE}/reels`)
            .then(r => r.json())
            .then(data => { if (Array.isArray(data)) setReels(data) })
            .catch(console.error)
            .finally(() => setLoading(false))
    }, [])

    const getEmbedUrl = (url: string) => {
        if (!url) return '';
        let embedUrl = url;
        if (url.includes('instagram.com')) {
            let id = '';
            const parts = url.split('/');
            const reelsIndex = parts.indexOf('reels');
            const reelIndex = parts.indexOf('reel');
            const pIndex = parts.indexOf('p');
            if (reelsIndex !== -1) id = parts[reelsIndex + 1];
            else if (reelIndex !== -1) id = parts[reelIndex + 1];
            else if (pIndex !== -1) id = parts[pIndex + 1];
            if (id) {
                id = id.split('?')[0];
                embedUrl = `https://www.instagram.com/reel/${id}/embed/`;
            }
        } else if (url.includes('youtube.com/watch?v=')) {
            embedUrl = url.replace('watch?v=', 'embed/');
        } else if (url.includes('youtu.be/')) {
            embedUrl = url.replace('youtu.be/', 'youtube.com/embed/');
        } else if (url.includes('youtube.com/shorts/')) {
            embedUrl = url.replace('shorts/', 'embed/');
        }
        
        // Add autoplay and parameters
        const connector = embedUrl.includes('?') ? '&' : '?';
        return `${embedUrl}${connector}autoplay=1&rel=0`;
    }

    const settings = React.useMemo(() => ({
        dots: true,
        infinite: reels.length > 6,
        speed: 800,
        slidesToShow: 6,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 4000,
        pauseOnHover: true,
        arrows: true,
        nextArrow: <NextArrow />,
        prevArrow: <PrevArrow />,
        cssEase: "cubic-bezier(0.23, 1, 0.32, 1)",
        responsive: [
            {
                breakpoint: 1500,
                settings: { slidesToShow: 5 }
            },
            {
                breakpoint: 1200,
                settings: { slidesToShow: 4 }
            },
            {
                breakpoint: 992,
                settings: { slidesToShow: 3, arrows: false }
            },
            {
                breakpoint: 768,
                settings: { 
                    slidesToShow: 2.2, 
                    arrows: false,
                    centerMode: true,
                    centerPadding: '20px'
                }
            },
            {
                breakpoint: 480,
                settings: { 
                    slidesToShow: 1, 
                    arrows: false,
                    centerMode: true,
                    centerPadding: '0px',
                    infinite: reels.length > 1
                }
            }
        ]
    }), [reels.length]);

    if (loading) return null;
    if (reels.length === 0) return null;

    return (
        <div className="reels-perfect-ui pb-4 md:pb-8 relative">
            <Slider {...settings} className="reels-premium-slider">
                {reels.map((reel) => (
                    <div key={reel._id} className="px-2 pb-0 outline-none mt-2">
                        <motion.div 
                            className="group relative aspect-[9/16] rounded-2xl md:rounded-3xl overflow-hidden bg-slate-900 border border-white/10 cursor-pointer shadow-none hover:shadow-[0_20px_50px_rgba(0,102,255,0.25)] transition-all duration-700 hover:-translate-y-2"
                            onClick={() => setSelectedReel(reel)}
                            whileHover={{ scale: 1.02 }}
                        >
                            {/* Visual Engine */}
                            <img
                                src={getImageUrl(reel.thumbnail)}
                                alt={reel.title}
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110 brightness-[0.85] group-hover:brightness-100"
                            />
                            
                            {/* Layered Gradients */}
                            <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/60 to-transparent z-10"></div>
                            <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black/80 via-black/40 to-transparent z-10"></div>

                            {/* Top Metadata */}
                            <div className="absolute top-4 left-0 right-0 px-4 flex justify-between items-start z-20">
                                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-2 py-0.5 flex items-center gap-1.5">
                                    <div className="w-1 h-1 bg-red-500 rounded-full animate-pulse"></div>
                                    <span className="text-[8px] font-black text-white tracking-widest uppercase">Live</span>
                                </div>
                                <div className="bg-black/20 backdrop-blur-md border border-white/10 rounded-full px-2 py-0.5 flex items-center gap-1">
                                    <i className="fa-solid fa-eye text-blue-400 text-[8px]"></i>
                                    <span className="text-[9px] font-bold text-white uppercase tracking-tighter">{(Math.random() * 8 + 2).toFixed(1)}K</span>
                                </div>
                            </div>

                            {/* Centered Play Button (Hover) */}
                            <div className="absolute inset-0 flex items-center justify-center z-20 opacity-0 group-hover:opacity-100 transition-all duration-500 transform scale-75 group-hover:scale-100">
                                <div className="w-12 h-12 bg-white/20 backdrop-blur-xl border border-white/30 rounded-full flex items-center justify-center shadow-2xl">
                                    <i className="fa-solid fa-play text-white text-xl ml-1"></i>
                                </div>
                            </div>

                            {/* Bottom Info */}
                            <div className="absolute bottom-0 left-0 right-0 p-3 md:p-5 z-20 transform group-hover:translate-y-[-5px] transition-transform duration-500">
                                <h5 className="text-white text-sm md:text-base font-black leading-tight mb-1 drop-shadow-lg line-clamp-2 uppercase tracking-wide">{reel.title}</h5>
                                <div className="flex items-center gap-1.5">
                                    <div className="p-1 bg-[#f1ae24]/20 rounded-md border border-[#f1ae24]/30">
                                        <i className="fa-solid fa-location-dot text-[#f1ae24] text-[8px] md:text-[9px]"></i>
                                    </div>
                                    <span className="text-white/80 text-[8px] md:text-[9px] font-bold uppercase tracking-widest">{reel.location || 'Premium Spot'}</span>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                ))}
            </Slider>

            {/* Immersive Playback Modal */}
            <AnimatePresence>
                {selectedReel && (
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl"
                        onClick={() => setSelectedReel(null)}
                    >
                        {/* Premium Close Button - Moved closer to the video */}
                        <motion.button 
                            className="absolute top-4 right-4 md:top-10 md:right-10 w-10 h-10 md:w-12 md:h-12 bg-white/10 hover:bg-white text-white hover:text-black rounded-full flex items-center justify-center transition-all border border-white/20 z-[100] backdrop-blur-md"
                            whileHover={{ scale: 1.1, rotate: 90 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedReel(null);
                            }}
                        >
                            <i className="fa-solid fa-xmark text-xl"></i>
                        </motion.button>

                        <motion.div 
                            initial={{ scale: 0.9, y: 40, opacity: 0 }}
                            animate={{ scale: 1, y: 20, opacity: 1 }}
                            exit={{ scale: 0.9, y: 40, opacity: 0 }}
                            transition={{ type: "spring", damping: 25, stiffness: 200 }}
                            className="relative w-full max-w-[340px] md:max-w-[380px] h-auto max-h-[85vh] aspect-[9/16] bg-slate-900 rounded-[2.5rem] overflow-hidden shadow-[0_0_80px_rgba(241,174,36,0.3)] border-[6px] border-slate-800"
                            onClick={e => e.stopPropagation()}
                        >
                            {/* Phone Notch Placeholder */}
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-5 bg-slate-800 rounded-b-2xl z-50"></div>

                            <iframe
                                width="100%"
                                height="100%"
                                src={getEmbedUrl(selectedReel.videoUrl)}
                                title={selectedReel.title}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                className="w-full h-full"
                            ></iframe>

                            {/* Info Overlay in Modal */}
                            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="bg-red-600 text-white text-[8px] font-black px-1.5 py-0.5 rounded animate-pulse uppercase tracking-widest">Live</div>
                                    <span className="text-white/60 text-[9px] font-bold uppercase tracking-widest">{selectedReel.location || 'Premium Spot'}</span>
                                </div>
                                <h4 className="text-white text-sm font-black leading-tight uppercase tracking-wide truncate">{selectedReel.title}</h4>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <style>{`
                .reels-premium-slider .slick-track {
                    display: flex !important;
                    align-items: center;
                }
                .reels-premium-slider .slick-dots {
                    bottom: -20px;
                }
                .reels-premium-slider .slick-dots li button:before {
                    color: #05144b;
                    font-size: 10px;
                    opacity: 0.15;
                    transition: all 0.3s;
                }
                .reels-premium-slider .slick-dots li.slick-active button:before {
                    color: #3b82f6;
                    opacity: 1;
                    font-size: 12px;
                }
                .line-clamp-2 {
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
            `}</style>
        </div>
    );
}
